import React from "react";
import { AsDateTime, AsPrice } from "src/components/ui/formatters";
import { Link } from "react-router-dom";
import { Button } from "src/components/ui/button";
import { useProfileHasPermission } from "src/hooks/profile";
import { MembershipPermission } from "src/enums/permission";
import { useAccountAgentListRequest } from "src/hooks/api/admin/account";
import { useAgentMarketCampaignUpdateRequest } from "src/hooks/api/agent/market-campaign";
import { Badge } from "src/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "src/components/ui/card";
import { Separator } from "src/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";
import { Calendar, DollarSign, MapPin, User, Phone, Mail, Clock, FileText, Building2, CreditCard, Users, Target } from "lucide-react";
import lazyWithRetry from "src/utils/lazy-with-retry";

interface MarketCampaignCardProps {
  className?: string;
  marketCampaign: AgentAPI.MarketCampaign.MarketCampaignData;
  showLink?: boolean;
  onUpdated?: () => void;
}

const MarketCampaignCard: React.FC<MarketCampaignCardProps> = ({ className, marketCampaign, showLink = true, onUpdated }) => {
  const canAssignAgent = useProfileHasPermission(MembershipPermission.ACCESS_MARKET_CAMPAIGN_AGENT_ASSIGN);
  const hasRejectMarketCampaignPerm = useProfileHasPermission(MembershipPermission.ACCESS_MARKET_CAMPAIGN_REJECT);
  const hasExecutionPerm = useProfileHasPermission(MembershipPermission.ACCESS_AGENT_MARKET_CAMPAIGN_EXECUTION);

  const canRejectMarketCampaign = React.useMemo(
    () => hasRejectMarketCampaignPerm && !marketCampaign.is_rejected && marketCampaign.paid_order?.is_completed,
    [hasRejectMarketCampaignPerm, marketCampaign.is_rejected, marketCampaign.paid_order?.is_completed],
  );

  const canCreateParserExecutions = React.useMemo(() => {
    // Don't show parser execution if campaign is unpaid
    if (!marketCampaign.paid_order?.is_completed) {
      return false;
    }

    if (marketCampaign.budget_type === "total") {
      return true;
    }

    return hasExecutionPerm && marketCampaign.executions_count === 0 && marketCampaign.is_active;
  }, [
    hasExecutionPerm,
    marketCampaign.executions_count,
    marketCampaign.paid_order?.is_completed,
    marketCampaign.budget_type,
    marketCampaign.executions_count,
    marketCampaign.is_active,
  ]);

  const RejectModal = React.useMemo(() => {
    if (canRejectMarketCampaign) {
      return lazyWithRetry(() => import("src/pages/agent/market-campaigns/components/MarketCampaignRejectModal"));
    }

    return null;
  }, [canRejectMarketCampaign]);

  const ParserExecutionModal = React.useMemo(() => {
    if (canCreateParserExecutions) {
      return lazyWithRetry(() => import("src/pages/agent/market-campaigns/components/MarketCampaignParserExecutionModal"));
    }

    return null;
  }, [canCreateParserExecutions]);

  const { data: agentData, refetch } = useAccountAgentListRequest({ limit: 50 }, false);

  const { mutate, data: mutateData, isSuccess: mutateSuccess } = useAgentMarketCampaignUpdateRequest(marketCampaign.id);

  React.useEffect(() => {
    refetch().finally();
  }, [canAssignAgent]);

  const agents = React.useMemo(() => {
    if (!agentData) {
      return [];
    }

    return agentData.data.results.map((agent) => ({
      value: agent.id,
      label: `${agent.name} (${agent.email})`,
    }));
  }, [agentData]);

  const handleAgentChange = (selectedAgentId: any) => {
    mutate({ agent: selectedAgentId });
  };

  React.useEffect(() => {
    if (mutateSuccess && mutateData?.data.id && onUpdated) {
      onUpdated();
    }
  }, [mutateSuccess, mutateData, onUpdated]);

  const getStatusBadge = () => {
    if (marketCampaign.is_active) {
      return <Badge className="bg-green-100 text-green-800 hover:bg-green-200 border-green-200">Active</Badge>;
    }
    if (
      !marketCampaign.is_active &&
      !marketCampaign.is_rejected &&
      !marketCampaign.is_user_removed &&
      marketCampaign.paid_order?.is_completed
    ) {
      return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-200">Completed</Badge>;
    }
    if (
      !marketCampaign.is_active &&
      !marketCampaign.is_rejected &&
      marketCampaign.is_user_removed &&
      marketCampaign.paid_order?.is_completed
    ) {
      return <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-200 border-orange-200">User removed</Badge>;
    }
    if (
      !marketCampaign.is_active &&
      marketCampaign.is_rejected &&
      marketCampaign.paid_order?.is_completed &&
      !marketCampaign.is_user_removed
    ) {
      return <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-200 border-orange-200">Rejected</Badge>;
    }
    if (!marketCampaign.is_active && !marketCampaign.paid_order?.is_completed) {
      return <Badge className="bg-red-100 text-red-800 hover:bg-red-200 border-red-200">Unpaid</Badge>;
    }
    return null;
  };

  return (
    <Card
      className={`border w-full h-full group hover:shadow-lg transition-all duration-300 shadow-sm bg-background backdrop-blur-sm flex flex-col ${className}`}
    >
      <CardHeader className="p-3 flex-shrink-0">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-lg font-medium text-gray-900">
              {marketCampaign.market.name}, {marketCampaign.market_state.code}
            </CardTitle>
          </div>
          {getStatusBadge()}
        </div>
        <CardDescription className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
          {showLink && (
            <Link to={`/agent/market-campaigns/detail/${marketCampaign.id}`} className="w-full">
              <Button variant="secondary" size="sm" className="hover:bg-gray-100 active:scale-95 transition-all duration-200 w-full">
                <Building2 className="h-4 w-4 mr-2" />
                Open workspace
              </Button>
            </Link>
          )}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4 p-3 flex-1 flex flex-col">
        <div className="flex-1 space-y-4">
          {/* Campaign Timeline */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground tracking-normal">
              <Clock className="h-4 w-4" />
              Campaign Timeline
            </div>
            <div className="grid grid-cols-1 gap-2 pl-3">
              {marketCampaign.budget_type !== "total" && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Ends at:</span>
                  <span className="text-xs font-medium">
                    <AsDateTime value={marketCampaign.ends_at} />
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Lease timeline:</span>
                <span className="text-xs font-medium">{marketCampaign.lease_timeline_display}</span>
              </div>
            </div>
          </div>

          {/* Owner Information */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground tracking-normal">
              <User className="h-4 w-4" />
              Owner Information
            </div>
            <div className="grid grid-cols-1 gap-2 pl-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Name:</span>
                <span className="text-xs font-medium">{marketCampaign.requested_by.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Email:</span>
                <span className="text-xs font-medium">{marketCampaign.requested_by.email}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Phone:</span>
                <span className="text-xs font-medium">{marketCampaign.requested_by.phone_number}</span>
              </div>
            </div>
          </div>

          {/* Budget Information */}
          {marketCampaign.budget_type !== "monthly" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground tracking-normal">
                <DollarSign className="h-4 w-4" />
                Budget Information
              </div>
              <div className="grid grid-cols-1 gap-2 pl-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Max budget:</span>
                  <AsPrice className="text-xs font-semibold text-green-600" value={marketCampaign.max_budget} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Budget:</span>
                  <AsPrice className="text-xs font-semibold text-blue-600" value={marketCampaign.budget} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Budget type:</span>
                  <span className="text-xs font-medium">{marketCampaign.budget_type === "total" ? "Budget based" : "Monthly"}</span>
                </div>
              </div>
            </div>
          )}

          {marketCampaign.notes && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground tracking-normal">
                <FileText className="h-4 w-4" />
                Notes
              </div>
              <div className="pl-3">
                <p className="text-xs text-muted-foreground leading-relaxed">{marketCampaign.notes}</p>
              </div>
            </div>
          )}

          {/* Payment Information */}
          {canAssignAgent && marketCampaign.paid_order && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground tracking-normal">
                <CreditCard className="h-4 w-4" />
                Payment Information
              </div>
              <div className="grid grid-cols-1 gap-2 pl-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Order amount:</span>
                  <AsPrice className="text-xs font-semibold text-purple-600" value={marketCampaign.paid_order.total} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Payment status:</span>
                  <span className="text-xs font-medium">{marketCampaign.paid_order.payment_status_display}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Parser tasks:</span>
                  <span className="text-xs font-medium">{marketCampaign.executions_count}</span>
                </div>
              </div>
            </div>
          )}

          {/* Agent Assignment */}
          {canAssignAgent && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground tracking-normal">
                <Users className="h-4 w-4" />
                Agent Assignment
              </div>
              <div className="space-y-3 pl-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Current agent:</span>
                  <span className="text-xs font-medium">{marketCampaign.agent?.name || "Not assigned"}</span>
                </div>
                {marketCampaign.agent && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Email:</span>
                    <span className="text-xs font-medium">{marketCampaign.agent.email}</span>
                  </div>
                )}
                {!marketCampaign.paid_order?.is_completed && !marketCampaign.agent && (
                  <div className="text-xs text-amber-600 bg-amber-50 p-3 rounded-sm border border-amber-200">
                    Assign agent will be available once order will be paid.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Agent Selection */}
          {canAssignAgent && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs font-medium text-foreground tracking-normal">
                <Target className="h-4 w-4" />
                Assign agent
              </div>
              <Select onValueChange={handleAgentChange} disabled={!marketCampaign.paid_order?.is_completed}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={marketCampaign?.agent ? "Change agent" : "Assign agent"} />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.value} value={String(agent.value)}>
                      {agent.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardContent>

      {/* Footer Actions */}
      {(showLink || canRejectMarketCampaign || canCreateParserExecutions) && (
        <CardFooter className="flex items-center gap-3 pt-6 p-3 flex-shrink-0">
          <div className="flex items-center gap-2 ml-auto">
            {canRejectMarketCampaign && RejectModal && (
              <React.Suspense
                fallback={
                  <Button variant="outline" size="sm" disabled>
                    Loading...
                  </Button>
                }
              >
                <RejectModal marketCampaign={marketCampaign} />
              </React.Suspense>
            )}
            {canCreateParserExecutions && ParserExecutionModal && (
              <React.Suspense
                fallback={
                  <Button variant="outline" size="sm" disabled>
                    Loading...
                  </Button>
                }
              >
                <ParserExecutionModal marketCampaign={marketCampaign} />
              </React.Suspense>
            )}
          </div>
        </CardFooter>
      )}
    </Card>
  );
};

export default MarketCampaignCard;
